<script>
import VSelectSearch from '../VSelectSearch';
import { http } from '../../../common/http';
import { ref, computed } from 'vue';

export default {
    name: 'VPermanentPatient',
    components: { VSelectSearch },
    props: {
        clinicId: {
            type: [Number, String],
            required: true,
        },
        selected: {
            type: [Object, Number],
            required: false,
        },
        name: {
            type: String,
            required: false,
            default: 'permanent_patient',
        },
        placeholder: {
            type: String,
            required: false,
            default: 'Search permanent patients',
        },
        required: {
            type: Boolean,
            required: false,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        label: {
            type: String,
            required: false,
            default: 'Search permanent patients',
        },
    },
    emits: ['change'],
    setup(props, { emit }) {
        const isLoading = ref(false);
        const searchResults = ref([]);
        const initialData = ref([]);

        /**
         * Search permanent patients by name
         * @param {string} query - Search query from user input
         * @param {Array} data - Initial data array (not used for API search)
         * @returns {Promise<Array>} - Search results
         */
        const searchPermanentPatients = async (query, data) => {
            if (query.length < 3) {
                return [];
            }

            isLoading.value = true;

            initialData.value = data;
            try {
                const locale = document.documentElement.lang || 'en';
                const response = await http.get(`/api/${locale}/permanent-patients/search`, {
                    params: {
                        clinic_id: props.clinicId,
                        query: query,
                    },
                });

                searchResults.value = response.data.data || [];
                return searchResults.value;
            } catch (error) {
                console.error('Error searching permanent patients:', error);
                return [];
            } finally {
                isLoading.value = false;
            }
        };

        /**
         * Handle selection change
         * @param {Object} selectedPatient - Selected patient object
         */
        const onChange = (selectedPatient) => {
            emit('change', selectedPatient);
        };

        return {
            searchPermanentPatients,
            onChange,
            isLoading,
            initialData,
        };
    },
};
</script>

<template>
    <label :for="name" class="v-select-search__label">{{ label }}</label>
    <v-select-search
        :placeholder="placeholder"
        :data="initialData"
        :selected="selected"
        :id="name"
        :name="name"
        :required="required"
        :disabled="disabled"
        :search="searchPermanentPatients"
        :min-length="3"
        :debounce-time="300"
        :readonly="false"
        @change="onChange"
    >
        <template #value="{ value }">
            <div class="flex-center flex--spread mr-12">
                <span>{{ value.fullName }}</span>
                <span v-if="value.clinic" class="text-muted">{{ value.clinic }}</span>
            </div>
        </template>

        <template #item="{ item }">
            <div class="flex-center flex--spread">
                <span>{{ item.fullName }}</span>
                <span v-if="item.clinic" class="text-muted">{{ item.clinic }}</span>
            </div>
        </template>

        <template #empty-state>
            <div class="v-select-search__empty-state">
                <span v-if="isLoading">Searching...</span>
                <span v-else>No permanent patients found</span>
            </div>
        </template>
    </v-select-search>
</template>

<style scoped>
.v-select-search__empty-state {
    padding: 12px;
    text-align: center;
    color: #666;
    font-style: italic;
}

.text-muted {
    color: #666;
    font-size: 0.9em;
}
</style>
